import { getFirestore, collection, query, getDocs, orderBy, updateDoc, deleteDoc, doc, limit, startAfter, getCountFromServer } from "firebase/firestore";
import { app } from "../firebase-config.js";

const db = getFirestore(app);

export const AdminContentService = {
    /**
     * Fetch paginated series list
     * @param {number} pageSize 
     * @param {Object|null} lastDocRef - For cursor pagination (simple)
     * OR we can implementation offset pagination if listing is small enough, 
     * but Firestore optimized is cursor.
     * However, for "Index" calculation and custom pagination UI (Page 1, 2, 3), 
     * client-side pagination is often easier if data set is < 1000.
     * User complained about "Index and Pagination".
     * If we use real firestore pagination, "Page 3" is hard without knowing start point of Page 3.
     * Strategy: Fetch ALL meta data (lean) or just Fetch ALL if < 2000?
     * Current code fetches ALL. 
     * Let's stick to Fetch ALL for now (to support Sort/Filter later easily) but implement Client-Side Pagination 
     * to solve the UI performance and "Index" requirement.
     * This avoids complex Firestore cursor logic for simple Admin Dashboard.
     */
    getAllSeries: async () => {
        try {
            const q = query(collection(db, "series"), orderBy("createdAt", "desc"));
            const snapshot = await getDocs(q);
            return snapshot.docs.map((doc, index) => ({
                id: doc.id,
                ...doc.data(),
                _index: snapshot.size - index // Reverse Index (Newest = Max, Oldest = 1)? Or simple Row Index?
                // User asked for "List Index Number". usually 1, 2, 3...
                // But in reverse chronological, usually Newest is #1 or Newest is #Total?
                // Let's typically do: (Total - CurrentIndex) for Descending lists so #1 is the oldest?
                // Or just 1, 2, 3 displayed in table.
            }));
        } catch (e) {
            console.error("Content Fetch Error:", e);
            throw e;
        }
    },

    updateSeries: async (id, data) => {
        const ref = doc(db, "series", id);
        await updateDoc(ref, data);
    },

    deleteSeries: async (id) => {
        const ref = doc(db, "series", id);
        await deleteDoc(ref);
    },

    updateSEO: async (id, keywords, geo) => {
        const ref = doc(db, "series", id);
        await updateDoc(ref, {
            seoKeywords: keywords,
            geoSynopsis: geo
        });
    },

    // [NEW] Cascade Approval (Series + Pending Episodes)
    approveSeriesAndEpisodes: async (id) => {
        const { writeBatch, where } = await import("firebase/firestore");
        const batch = writeBatch(db);

        // 1. Approve Series
        const seriesRef = doc(db, "series", id);
        batch.update(seriesRef, {
            status: 'published',
            publishedAt: new Date().toISOString(),
            updatedAt: new Date() // Force refresh
        });

        // 2. Approve Pending Episodes
        // We need to support ALL content types that use 'episodes' subcollection (Webtoon, Webnovel, DaVinci)
        try {
            const episodesRef = collection(db, "series", id, "episodes");
            // Only fetch pending ones to save writes
            const q = query(episodesRef, where("status", "==", "pending"));
            const snaps = await getDocs(q);

            snaps.forEach(snap => {
                batch.update(snap.ref, { status: 'published' });
            });
            console.log(`[Admin] Approving ${snaps.size} pending episodes for series ${id}`);
        } catch (e) {
            console.warn("[Admin] Episode approval warning (maybe no subcollection):", e);
        }

        await batch.commit();
    },

};
